import { geometry } from '@progress/kendo-drawing';
import { Polar } from './Polar';
import { isFunction } from './../utils';
const HITTESTAREA = 3;
/**
 * Represents a 2D point with x and y coordinates.
 * Extends the Kendo Drawing geometry Point class with additional utility methods.
 */
export class Point extends geometry.Point {
    /**
     * Creates a new Point instance.
     * @param x The x-coordinate of the point (defaults to 0)
     * @param y The y-coordinate of the point (defaults to 0)
     */
    constructor(x, y) {
        super(x, y);
    }
    /**
     * Creates a copy of this point.
     * @returns A new Point instance with the same coordinates
     */
    clone() {
        return new Point(this.x, this.y);
    }
    /**
     * Adds another point to this point.
     * @param p The point to add
     * @returns A new Point representing the sum of the two points
     */
    plus(p) {
        return new Point(this.x + p.x, this.y + p.y);
    }
    /**
     * Subtracts another point from this point.
     * @param p The point to subtract
     * @returns A new Point representing the difference
     */
    minus(p) {
        return new Point(this.x - p.x, this.y - p.y);
    }
    /**
     * Creates a new point offset by the specified value in both x and y directions.
     * @param value The offset value to subtract from both coordinates
     * @returns A new Point offset by the specified amount
     */
    offset(value) {
        return new Point(this.x - value, this.y - value);
    }
    /**
     * Multiplies this point by a scalar value.
     * @param s The scalar multiplier
     * @returns A new Point with coordinates multiplied by the scalar
     */
    times(s) {
        return new Point(this.x * s, this.y * s);
    }
    /**
     * Returns a normalized version of this point (unit vector).
     * @returns A new Point with length 1 in the same direction, or origin if length is 0
     */
    normalize() {
        if (this.length() === 0) {
            return new Point();
        }
        return this.times(1 / this.length());
    }
    /**
     * Calculates the length (magnitude) of this point vector.
     * @returns The distance from origin to this point
     */
    length() {
        return Math.sqrt(this.x * this.x + this.y * this.y);
    }
    /**
     * Returns a string representation of this point.
     * @returns A string in the format "(x,y)"
     */
    toString() {
        return '(' + this.x + ',' + this.y + ')';
    }
    /**
     * Calculates the squared length of this point vector.
     * More efficient than length() when only comparing distances.
     * @returns The squared distance from origin to this point
     */
    lengthSquared() {
        return (this.x * this.x + this.y * this.y);
    }
    /**
     * Calculates the midpoint between two points.
     * @param p The first point
     * @param q The second point
     * @returns A new Point representing the midpoint between p and q
     */
    middleOf(p, q) {
        return new Point(q.x - p.x, q.y - p.y).times(0.5).plus(p);
    }
    /**
     * Converts this point to polar coordinates.
     * @param useDegrees Whether to return angle in degrees (true) or radians (false)
     * @returns A Polar object with radius and angle
     */
    toPolar(useDegrees) {
        let factor = 1;
        if (useDegrees) {
            factor = 180 / Math.PI;
        }
        const a = Math.atan2(Math.abs(this.y), Math.abs(this.x));
        const halfpi = Math.PI / 2;
        const len = this.length();
        if (this.x === 0) {
            // note that the angle goes down and not the usual mathematical convention
            if (this.y === 0) {
                return new Polar(0, 0);
            }
            if (this.y > 0) {
                return new Polar(len, factor * halfpi);
            }
            if (this.y < 0) {
                return new Polar(len, factor * 3 * halfpi);
            }
        }
        else if (this.x > 0) {
            if (this.y === 0) {
                return new Polar(len, 0);
            }
            if (this.y > 0) {
                return new Polar(len, factor * a);
            }
            if (this.y < 0) {
                return new Polar(len, factor * (4 * halfpi - a));
            }
        }
        else {
            if (this.y === 0) {
                return new Polar(len, 2 * halfpi);
            }
            if (this.y > 0) {
                return new Polar(len, factor * (2 * halfpi - a));
            }
            if (this.y < 0) {
                return new Polar(len, factor * (2 * halfpi + a));
            }
        }
    }
    /**
     * Determines if this point lies on the line segment between two other points.
     * @param from The starting point of the line segment
     * @param to The ending point of the line segment
     * @returns True if this point is on the line segment, false otherwise
     */
    isOnLine(from, to) {
        if (from.x > to.x) { // from must be the leftmost point
            const temp = to;
            to = from;
            from = temp;
        }
        const r1 = new Rect(from.x, from.y).inflate(HITTESTAREA, HITTESTAREA), r2 = new Rect(to.x, to.y).inflate(HITTESTAREA, HITTESTAREA);
        let o1, u1;
        if (r1.union(r2).contains(this)) {
            if (from.x === to.x || from.y === to.y) {
                return true;
            }
            else if (from.y < to.y) {
                o1 = r1.x + (((r2.x - r1.x) * (this.y - (r1.y + r1.height))) / ((r2.y + r2.height) - (r1.y + r1.height)));
                u1 = (r1.x + r1.width) + ((((r2.x + r2.width) - (r1.x + r1.width)) * (this.y - r1.y)) / (r2.y - r1.y));
            }
            else {
                o1 = r1.x + (((r2.x - r1.x) * (this.y - r1.y)) / (r2.y - r1.y));
                u1 = (r1.x + r1.width) + ((((r2.x + r2.width) - (r1.x + r1.width)) * (this.y - (r1.y + r1.height))) / ((r2.y + r2.height) - (r1.y + r1.height)));
            }
            return (this.x > o1 && this.x < u1);
        }
        return false;
    }
    /**
     * Parses a point from a string representation.
     * @param str The string to parse, expected format: "(x,y)"
     * @returns A new Point if parsing succeeds, undefined otherwise
     */
    parse(str) {
        const tempStr = str.slice(1, str.length - 1), xy = tempStr.split(','), x = parseInt(xy[0], 10), y = parseInt(xy[1], 10);
        if (!isNaN(x) && !isNaN(y)) {
            return new Point(x, y);
        }
    }
}
/**
 * Represents a rectangle with position and dimensions.
 * Provides methods for geometric operations and transformations.
 */
export class Rect {
    /**
     * Creates a new Rect instance.
     * @param x The x-coordinate of the top-left corner (defaults to 0)
     * @param y The y-coordinate of the top-left corner (defaults to 0)
     * @param width The width of the rectangle (defaults to 0)
     * @param height The height of the rectangle (defaults to 0)
     */
    constructor(x, y, width, height) {
        this.x = x || 0;
        this.y = y || 0;
        this.width = width || 0;
        this.height = height || 0;
    }
    /**
     * Determines if a point is contained within this rectangle.
     * @param point The point to test
     * @returns True if the point is inside the rectangle, false otherwise
     */
    contains(point) {
        return ((point.x >= this.x) && (point.x <= (this.x + this.width)) && (point.y >= this.y) && (point.y <= (this.y + this.height)));
    }
    /**
     * Inflates the rectangle by the specified amounts.
     * @param dx The amount to inflate horizontally
     * @param dy The amount to inflate vertically (defaults to dx if not provided)
     * @returns This rectangle instance for chaining
     */
    inflate(dx, dy) {
        if (dy === undefined) {
            dy = dx;
        }
        this.x -= dx;
        this.y -= dy;
        this.width += 2 * dx + 1;
        this.height += 2 * dy + 1;
        return this;
    }
    /**
     * Moves the rectangle by the specified offset.
     * @param dx The horizontal offset (or a Point object)
     * @param dy The vertical offset (ignored if dx is a Point)
     * @returns This rectangle instance for chaining
     */
    offset(dx, dy) {
        let x = dx, y = dy;
        if (dx instanceof Point) {
            x = dx.x;
            y = dx.y;
        }
        this.x += x;
        this.y += y;
        return this;
    }
    /**
     * Creates a new rectangle that is the union of this rectangle and another.
     * @param r The rectangle to union with
     * @returns A new Rect representing the union of both rectangles
     */
    union(r) {
        const x1 = Math.min(this.x, r.x);
        const y1 = Math.min(this.y, r.y);
        const x2 = Math.max((this.x + this.width), (r.x + r.width));
        const y2 = Math.max((this.y + this.height), (r.y + r.height));
        return new Rect(x1, y1, x2 - x1, y2 - y1);
    }
    /**
     * Gets the center point of the rectangle.
     * @returns A Point representing the center of the rectangle
     */
    center() {
        return new Point(this.x + this.width / 2, this.y + this.height / 2);
    }
    /**
     * Gets the top center point of the rectangle.
     * @returns A Point at the top center edge
     */
    top() {
        return new Point(this.x + this.width / 2, this.y);
    }
    /**
     * Gets the right center point of the rectangle.
     * @returns A Point at the right center edge
     */
    right() {
        return new Point(this.x + this.width, this.y + this.height / 2);
    }
    /**
     * Gets the bottom center point of the rectangle.
     * @returns A Point at the bottom center edge
     */
    bottom() {
        return new Point(this.x + this.width / 2, this.y + this.height);
    }
    /**
     * Gets the left center point of the rectangle.
     * @returns A Point at the left center edge
     */
    left() {
        return new Point(this.x, this.y + this.height / 2);
    }
    /**
     * Gets the top-left corner point of the rectangle.
     * @returns A Point at the top-left corner
     */
    topLeft() {
        return new Point(this.x, this.y);
    }
    /**
     * Gets the top-right corner point of the rectangle.
     * @returns A Point at the top-right corner
     */
    topRight() {
        return new Point(this.x + this.width, this.y);
    }
    /**
     * Gets the bottom-left corner point of the rectangle.
     * @returns A Point at the bottom-left corner
     */
    bottomLeft() {
        return new Point(this.x, this.y + this.height);
    }
    /**
     * Gets the bottom-right corner point of the rectangle.
     * @returns A Point at the bottom-right corner
     */
    bottomRight() {
        return new Point(this.x + this.width, this.y + this.height);
    }
    /**
     * Creates a copy of this rectangle.
     * @returns A new Rect with the same position and dimensions
     */
    clone() {
        return new Rect(this.x, this.y, this.width, this.height);
    }
    /**
     * Determines if this rectangle is empty (zero width and height).
     * @returns True if the rectangle has no area, false otherwise
     */
    isEmpty() {
        return !this.width && !this.height;
    }
    /**
     * Determines if this rectangle is equal to another rectangle.
     * @param rect The rectangle to compare with
     * @returns True if both rectangles have the same position and dimensions
     */
    equals(rect) {
        return this.x === rect.x && this.y === rect.y && this.width === rect.width && this.height === rect.height;
    }
    /**
     * Calculates the bounding rectangle after rotating this rectangle by a given angle.
     * @param angle The rotation angle in radians
     * @returns A new Rect representing the axis-aligned bounding box of the rotated rectangle
     */
    rotatedBounds(angle) {
        const rect = this.clone(), points = this.rotatedPoints(angle), tl = points[0], tr = points[1], br = points[2], bl = points[3];
        rect.x = Math.min(br.x, tl.x, tr.x, bl.x);
        rect.y = Math.min(br.y, tl.y, tr.y, bl.y);
        rect.width = Math.max(br.x, tl.x, tr.x, bl.x) - rect.x;
        rect.height = Math.max(br.y, tl.y, tr.y, bl.y) - rect.y;
        return rect;
    }
    /**
     * Gets the four corner points of this rectangle after rotation.
     * @param angle The rotation angle in radians
     * @returns An array of four Points representing the rotated corners [topLeft, topRight, bottomRight, bottomLeft]
     */
    rotatedPoints(angle) {
        const c = this.center(), br = this.bottomRight().rotate(angle, c), tl = this.topLeft().rotate(angle, c), tr = this.topRight().rotate(angle, c), bl = this.bottomLeft().rotate(angle, c);
        return [tl, tr, br, bl];
    }
    /**
     * Returns a string representation of this rectangle.
     * @param delimiter The delimiter to use between values (defaults to space)
     * @returns A string in the format "x y width height" (or with custom delimiter)
     */
    toString(delimiter) {
        delimiter = delimiter || ' ';
        return this.x + delimiter + this.y + delimiter + this.width + delimiter + this.height;
    }
    /**
     * Scales this rectangle while maintaining a static reference point.
     * @param scaleX The horizontal scale factor
     * @param scaleY The vertical scale factor
     * @param staicPoint The static point that should remain fixed during scaling
     * @param adornerCenter The center point of the adorner
     * @param angle The rotation angle to apply during scaling
     */
    scale(scaleX, scaleY, staicPoint, adornerCenter, angle) {
        let tl = this.topLeft();
        const thisCenter = this.center();
        tl.rotate(angle, thisCenter).rotate(angle, adornerCenter);
        const delta = staicPoint.minus(tl);
        const scaled = new Point(delta.x * scaleX, delta.y * scaleY);
        const position = delta.minus(scaled);
        tl = tl.plus(position);
        tl.rotate(angle, adornerCenter).rotate(angle, thisCenter);
        this.x = tl.x;
        this.y = tl.y;
        this.width *= scaleX;
        this.height *= scaleY;
    }
    /**
     * Scales this rectangle by a zoom factor.
     * @param zoom The zoom factor to apply
     * @returns This rectangle instance for chaining
     */
    zoom(zoom) {
        this.x *= zoom;
        this.y *= zoom;
        this.width *= zoom;
        this.height *= zoom;
        return this;
    }
    /**
     * Determines if this rectangle overlaps with another rectangle.
     * @param rect The rectangle to check for overlap
     * @returns True if the rectangles overlap, false otherwise
     */
    overlaps(rect) {
        const bottomRight = this.bottomRight();
        const rectBottomRight = rect.bottomRight();
        const overlaps = !(bottomRight.x < rect.x || bottomRight.y < rect.y ||
            rectBottomRight.x < this.x || rectBottomRight.y < this.y);
        return overlaps;
    }
    /**
     * Converts an object to a Rect instance.
     * @param rect The object to convert (if not already a Rect)
     * @returns A Rect instance
     */
    static toRect(rect) {
        if (!(rect instanceof Rect)) {
            rect = new Rect(rect.x, rect.y, rect.width, rect.height);
        }
        return rect;
    }
    /**
     * Creates an empty rectangle (zero position and dimensions).
     * @returns A new empty Rect instance
     */
    static empty() {
        return new Rect(0, 0, 0, 0);
    }
    /**
     * Creates a rectangle from two points.
     * @param p The first point
     * @param q The second point
     * @returns A new Rect with the two points as opposite corners
     * @throws Error if any coordinate values are NaN
     */
    static fromPoints(p, q) {
        if (isNaN(p.x) || isNaN(p.y) || isNaN(q.x) || isNaN(q.y)) {
            throw new Error('Some values are NaN.');
        }
        return new Rect(Math.min(p.x, q.x), Math.min(p.y, q.y), Math.abs(p.x - q.x), Math.abs(p.y - q.y));
    }
}
/**
 * Aligns two rectangles, where one is the container and the other is content.
 */
export class RectAlign {
    constructor(container) {
        this.container = Rect.toRect(container);
    }
    align(content, alignment) {
        const alignValues = alignment.toLowerCase().split(' ');
        for (let i = 0; i < alignValues.length; i++) {
            content = this._singleAlign(content, alignValues[i]);
        }
        return content;
    }
    _singleAlign(content, alignment) {
        if (isFunction(this[alignment])) {
            return this[alignment](content);
        }
        else {
            return content;
        }
    }
    left(content) {
        return this._align(content, this._left);
    }
    center(content) {
        return this._align(content, this._center);
    }
    right(content) {
        return this._align(content, this._right);
    }
    stretch(content) {
        return this._align(content, this._stretch);
    }
    top(content) {
        return this._align(content, this._top);
    }
    middle(content) {
        return this._align(content, this._middle);
    }
    bottom(content) {
        return this._align(content, this._bottom);
    }
    _left(container, content) {
        content.x = container.x;
    }
    _center(container, content) {
        content.x = ((container.width - content.width) / 2) || 0;
    }
    _right(container, content) {
        content.x = container.width - content.width;
    }
    _top(container, content) {
        content.y = container.y;
    }
    _middle(container, content) {
        content.y = ((container.height - content.height) / 2) || 0;
    }
    _bottom(container, content) {
        content.y = container.height - content.height;
    }
    _stretch(container, content) {
        content.x = 0;
        content.y = 0;
        content.height = container.height;
        content.width = container.width;
    }
    _align(content, alignCalc) {
        content = Rect.toRect(content);
        alignCalc(this.container, content);
        return content;
    }
}
